import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../../api/axios';
import './ProspectFinderResults.css';
import LeadgenPreloader from '../../ui/LeadgenPreloader';
import Toast from '../../ui/Toast';

export default function ProspectFinderResults({
  prospects,
  loading,
  onBack,
  onRefresh,
  searchQuery,
  setSearchQuery,
  onSearch,
  requestId,
  userId,
  searchType,
  setSearchType,
  refreshProfile,
  searchProvider,
  setSearchProvider,
  hasMore,
  onLoadMore,
}) {
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [enriching, setEnriching] = useState(false);
  const [useHunterFallback, setUseHunterFallback] = useState(false);
  const [openCompanyRowId, setOpenCompanyRowId] = useState(null);
  const [enrichDropdownOpen, setEnrichDropdownOpen] = useState(false);
  const [savedProspects, setSavedProspects] = useState({});
  const [savedCompaniesMap, setSavedCompaniesMap] = useState({});
  const [enrichedCompaniesMap, setEnrichedCompaniesMap] = useState({});
  const [enrichingCompanyId, setEnrichingCompanyId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({ title: '', message: '', onConfirm: null });

  const enrichDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (enrichDropdownRef.current && !enrichDropdownRef.current.contains(e.target)) {
        setEnrichDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSavedProspects = useCallback(async () => {
    try {
      const response = await axios.get('/prospects');
      if (response.data.success) {
        const map = {};
        const companyMap = {};
        response.data.data.forEach((p) => {
          if (p.lusha_id != null) map[String(p.lusha_id)] = p;
          if (p.company_id && p.company_details) {
            companyMap[String(p.company_id)] = {
              ...(companyMap[String(p.company_id)] || {}),
              ...p.company_details,
              companyId: p.company_id,
              name: p.company_details.companyName || p.company_name || null,
            };
          }
          if (p.company_name && p.company_details) {
            const nameKey = String(p.company_name).trim().toLowerCase();
            companyMap[nameKey] = {
              ...(companyMap[nameKey] || {}),
              ...p.company_details,
              companyId: p.company_id || p.company_details.companyId || null,
              name: p.company_details.companyName || p.company_name || null,
            };
          }
        });
        setSavedProspects(map);
        setSavedCompaniesMap(companyMap);
      }
    } catch (error) {
      console.error('Error fetching saved prospects:', error);
    }
  }, []);

  useEffect(() => {
    if (prospects.length > 0) fetchSavedProspects();
  }, [prospects, enriching, fetchSavedProspects]);

  const showToast = (message, type = 'error') => {
    setToast({ show: true, message, type });
  };

  const clearSearch = () => setSearchQuery('');

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedProspects(prospects.map((p) => getProspectData(p).id));
    } else {
      setSelectedProspects([]);
    }
  };

  const handleSelectProspect = (prospectId) => {
    setSelectedProspects((prev) =>
      prev.includes(prospectId) ? prev.filter((id) => id !== prospectId) : [...prev, prospectId]
    );
  };

  const handleEnrichSpecific = (contactId, enrichType = 'both') => {
    if (!requestId) {
      showToast('No requestId available. Please perform a new search first.');
      return;
    }
    const creditsNeeded = enrichType === 'email' ? 1 : enrichType === 'phone' ? 5 : 6;
    setConfirmConfig({
      title: 'Confirm Enrichment',
      message: `Enriching this contact will use ${creditsNeeded} credit${creditsNeeded > 1 ? 's' : ''}. Do you wish to continue?`,
      onConfirm: () => performSpecificEnrichment(contactId, enrichType),
    });
    setShowConfirmModal(true);
  };

  const performSpecificEnrichment = async (contactId, enrichType) => {
    setShowConfirmModal(false);
    setEnriching(true);
    try {
      const revealEmails = enrichType === 'email' || enrichType === 'both';
      const revealPhones = enrichType === 'phone' || enrichType === 'both';
      const endpoint =
        searchProvider === 'lusha' ? '/lusha/enrich/contacts' : '/apollo/enrich/people';
      const response = await axios.post(endpoint, {
        requestId,
        contactIds: [contactId],
        params: { person_id: contactId },
        userId,
        revealEmails,
        revealPhones,
        useHunterFallback,
      });
      if (response.data.success || response.status === 200) {
        showToast(
          `Successfully enriched contact! Saved ${response.data.contacts?.length || 0} contact(s).`,
          'success'
        );
        if (typeof refreshProfile === 'function') refreshProfile();
        setSavedProspects((prev) => {
          const newSaved = { ...prev };
          (response.data.contacts || []).forEach((c) => {
            if (c.isSuccess && c.data) {
              const cid = String(c.id || contactId);
              const existing = prev[cid] || {};
              newSaved[cid] = {
                ...existing,
                lusha_id: cid,
                name: c.data.fullName || c.data.name || existing.name,
                job_title: c.data.jobTitle || existing.job_title,
              };
              if (revealEmails) {
                newSaved[cid].email = c.data.emailAddresses || c.data.emails;
              }
              if (revealPhones) {
                newSaved[cid].phone_number = c.data.phoneNumbers || c.data.phones;
              }
            }
          });
          return newSaved;
        });
        fetchSavedProspects();
        onRefresh();
      } else {
        throw new Error(response.data.error || 'Enrichment failed');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        showToast(
          <>
            Insufficient credits.{' '}
            <a
              href="/billing"
              style={{ color: '#fff', textDecoration: 'underline', fontWeight: '800' }}
            >
              Buy Credits / Top up
            </a>
          </>
        );
      } else {
        const rawError =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Enrichment failed';
        const errorMsg =
          typeof rawError === 'object'
            ? JSON.stringify(rawError.message || rawError.details || rawError)
            : String(rawError);
        showToast(`Enrichment failed: ${errorMsg}`);
      }
    } finally {
      setEnriching(false);
    }
  };

  const handleEnrichProspects = (enrichType = 'both') => {
    if (!requestId) {
      showToast('No requestId available.');
      return;
    }
    if (selectedProspects.length === 0) {
      showToast('Please select at least one prospect to enrich.');
      return;
    }
    const creditsPerContact = enrichType === 'email' ? 1 : enrichType === 'phone' ? 5 : 6;
    const totalCredits = creditsPerContact * selectedProspects.length;
    setConfirmConfig({
      title: 'Bulk Enrichment',
      message: `Enriching ${selectedProspects.length} contacts (${enrichType}) will use ${totalCredits} credits. Do you wish to proceed?`,
      onConfirm: () => performBulkEnrichment(enrichType),
    });
    setShowConfirmModal(true);
    setEnrichDropdownOpen(false);
  };

  const performBulkEnrichment = async (enrichType) => {
    setShowConfirmModal(false);
    setEnriching(true);
    try {
      const revealEmails = enrichType === 'email' || enrichType === 'both';
      const revealPhones = enrichType === 'phone' || enrichType === 'both';
      const endpoint =
        searchProvider === 'lusha' ? '/lusha/enrich/contacts' : '/apollo/enrich/people';
      const response = await axios.post(endpoint, {
        requestId,
        contactIds: selectedProspects,
        userId,
        revealEmails,
        revealPhones,
        useHunterFallback,
      });
      if (response.data.success) {
        showToast(`Enrichment Successful! ${response.data.message || ''}`, 'success');
        if (typeof refreshProfile === 'function') refreshProfile();
        setSavedProspects((prev) => {
          const newSaved = { ...prev };
          (response.data.contacts || []).forEach((c) => {
            if (c.isSuccess && c.data) {
              const cid = String(c.id);
              newSaved[cid] = {
                lusha_id: cid,
                email: c.data.emailAddresses || c.data.emails,
                phone_number: c.data.phoneNumbers || c.data.phones,
                name: c.data.fullName || c.data.name,
                job_title: c.data.jobTitle,
              };
            }
          });
          return newSaved;
        });
        fetchSavedProspects();
        onRefresh();
      } else {
        showToast('Enrichment failed. Please try again.');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        showToast(
          <>
            Insufficient credits.{' '}
            <a
              href="/billing"
              style={{ color: '#fff', textDecoration: 'underline', fontWeight: '800' }}
            >
              Buy Credits / Top up
            </a>
          </>
        );
      } else {
        const rawError =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Enrichment failed';
        const errorMsg =
          typeof rawError === 'object'
            ? JSON.stringify(rawError.message || rawError.details || rawError)
            : String(rawError);
        showToast(`Enrichment Error: ${errorMsg}`);
      }
    } finally {
      setEnriching(false);
    }
  };

  const handleUnlockCompany = (item, rowId) => {
    if (!requestId) return;
    setConfirmConfig({
      title: 'Unlock Full Company Profile',
      message: 'This will use 1 credit to fetch the complete company details. Proceed?',
      onConfirm: () => performUnlockCompany(item, rowId),
    });
    setShowConfirmModal(true);
  };

  const performUnlockCompany = async (item, rowId) => {
    setShowConfirmModal(false);
    const coId = getCompanyIdentifier(item);
    if (!coId) return;
    setEnrichingCompanyId(rowId);
    try {
      const response = await axios.post('/lusha/enrich/companies', {
        requestId,
        companiesIds: [String(coId)],
        userId,
      });
      if (response.data.success && response.data.companies?.length > 0) {
        const enriched = response.data.companies[0];
        setEnrichedCompaniesMap((prev) => ({ ...prev, [String(coId)]: enriched }));
        if (response.data.creditsCharged > 0 && typeof refreshProfile === 'function')
          refreshProfile();
      }
    } catch (error) {
      const rawError =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Enrichment failed';
      const errorMsg =
        typeof rawError === 'object'
          ? JSON.stringify(rawError.message || rawError.details || rawError)
          : String(rawError);
      showToast(`Enrichment failed: ${errorMsg}`);
    } finally {
      setEnrichingCompanyId(null);
    }
  };

  const getCompanyIdentifier = (item) => {
    if (!item || typeof item !== 'object') return null;
    return (
      item.companyId ||
      item.company_id ||
      item.organization_id ||
      item.organization?.id ||
      item.id ||
      null
    );
  };

  const getProspectData = (prospect) => {
    const lookupId = String(prospect.contactId || prospect.id || '');
    let saved = lookupId ? savedProspects[lookupId] : null;
    if (!saved && (prospect.linkedinUrl || prospect.linkedin_url)) {
      const url = prospect.linkedinUrl || prospect.linkedin_url;
      saved = Object.values(savedProspects).find(
        (p) => p.social_links?.linkedin === url || p.linkedinUrl === url || p.linkedin_url === url
      );
    }
    const rowId = lookupId || String(Math.random());
    const contactId = prospect.contactId || prospect.id;
    const extractValue = (val) => {
      if (!val) return null;
      if (typeof val === 'string') return val;
      if (typeof val === 'object') {
        const email = val.email || val.value || val.email_address || val.emailAddress;
        const phone = val.phone || val.number || val.phone_number || val.phoneNumber || val.value;
        return email || phone || null;
      }
      return String(val);
    };

    const allEmails = [
      saved?.email,
      saved?.email_address,
      prospect.email,
      prospect.email_address,
      prospect.emailAddresses,
      prospect.emails,
    ]
      .flatMap((v) => {
        if (!v) return [];
        if (Array.isArray(v)) return v.map(extractValue).filter(Boolean);
        return [extractValue(v)];
      })
      .filter(Boolean);

    const allPhones = [
      saved?.phone,
      saved?.phone_number,
      prospect.phone,
      prospect.phone_number,
      prospect.phoneNumbers,
      prospect.phone_numbers,
    ]
      .flatMap((v) => {
        if (!v) return [];
        if (Array.isArray(v)) return v.map(extractValue).filter(Boolean);
        return [extractValue(v)];
      })
      .filter(Boolean);
    const normalizeDisplayValue = (value) => {
      if (value == null) return null;
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      if (Array.isArray(value))
        return value.map(normalizeDisplayValue).filter(Boolean).join(', ') || null;
      if (typeof value === 'object')
        return value.name || value.label || value.value || value.raw || null;
      return null;
    };
    const formatLoc = (loc) => {
      if (!loc) return null;
      if (typeof loc === 'string') return loc;
      if (typeof loc === 'object') {
        const parts = [loc.city, loc.state, loc.country].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : loc.raw_location || null;
      }
      return null;
    };
    return {
      id: rowId,
      contactId,
      name: normalizeDisplayValue(prospect.name || prospect.fullName) || '',
      jobTitle: normalizeDisplayValue(prospect.jobTitle || prospect.title) || '',
      companyId: prospect.companyId,
      companyName: normalizeDisplayValue(prospect.companyName || prospect.company?.name) || '',
      companyLogo: prospect.logoUrl || prospect.companyLogoUrl || prospect.company?.logoUrl || null,
      email: allEmails[0] || null,
      phone: allPhones[0] || null,
      location: formatLoc(prospect.location || prospect.contactLocation) || '',
      linkedinUrl: prospect.linkedinUrl || prospect.linkedin_url || null,
      isEmailRevealed: allEmails.length > 0,
      isPhoneRevealed: allPhones.length > 0,
      hasEmail: !!(prospect.hasEmails || prospect.hasWorkEmail || allEmails.length > 0),
      hasPhone: !!(prospect.hasPhones || prospect.hasDirectPhone || allPhones.length > 0),
    };
  };

  const getCompanyData = (company) => {
    const coRaw = company.company || company.organization || company;
    const companyId = getCompanyIdentifier(company);
    const coIdLookup = companyId ? String(companyId) : '';
    const coFromDb = (coIdLookup && savedCompaniesMap[coIdLookup]) || {};
    const coEnriched = (coIdLookup && enrichedCompaniesMap[coIdLookup]) || {};
    const co = { ...coRaw, ...coFromDb, ...coEnriched };
    const formatLoc = (loc) => {
      if (!loc) return null;
      if (typeof loc === 'string') return loc;
      if (typeof loc === 'object') {
        const parts = [loc.city, loc.state, loc.country].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : loc.raw_location || null;
      }
      return null;
    };
    const normalizeDisplayValue = (value) => {
      if (value == null) return null;
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      if (Array.isArray(value))
        return value.map(normalizeDisplayValue).filter(Boolean).join(', ') || null;
      if (typeof value === 'object')
        return value.name || value.label || value.value || value.raw || null;
      return null;
    };
    const formatRangeValue = (value) => {
      if (!value) return null;
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      if (typeof value === 'object') {
        if (value.raw) return String(value.raw);
        if (value.min !== undefined && value.max !== undefined) return `${value.min}-${value.max}`;
        if (value.min !== undefined) return `${value.min}+`;
        return value.name || value.label || null;
      }
      return null;
    };
    const rawLocation = company.location || co.location;
    const rawEmployees = co.employees || company.employees || co.company_size || co.employee_count;
    const rawRevenue = co.revenue || company.revenue || co.revenue_range || co.annual_revenue;
    const rawIndustry = co.mainIndustry || company.mainIndustry || co.industry;
    const rawSubIndustry =
      co.subIndustry || (typeof rawIndustry === 'object' ? rawIndustry.sub_industry : null);
    const rawFunding = co.funding || co.recent_funding_amount || co.total_funding_amount;
    const rawCity = co.city || co.location?.city;
    const rawCountry = co.country || co.location?.country;
    const rawType = co.companyType || co.type || co.company_type;
    return {
      id: coIdLookup || co.id || Math.random().toString(),
      companyName: normalizeDisplayValue(company.companyName || co.name) || '',
      description:
        normalizeDisplayValue(
          company.description || company.companyDescription || co.description
        ) || '',
      logoUrl: company.logoUrl || co.logo_url || null,
      domain: normalizeDisplayValue(company.fqdn || co.website) || '',
      isFresh: !!(co.isCached || co.isSuccess || co._from_cache || coEnriched._from_backend_enrich),
      location: formatLoc(rawLocation) || '',
      employeeCount: formatRangeValue(rawEmployees) || '',
      revenueValue: formatRangeValue(rawRevenue) || '',
      mainIndustry: normalizeDisplayValue(rawIndustry) || '',
      subIndustry: normalizeDisplayValue(rawSubIndustry),
      funding: normalizeDisplayValue(rawFunding),
      city: normalizeDisplayValue(rawCity),
      country: normalizeDisplayValue(rawCountry),
      companyType: normalizeDisplayValue(rawType),
      hasEmployeesCount: !!rawEmployees,
      hasRevenue: !!rawRevenue,
      hasIndustry: !!rawIndustry,
      hasSubIndustry: !!rawSubIndustry,
      hasCountry: !!rawCountry,
      hasFunding: !!rawFunding,
      hasCompanyType: !!rawType,
      hasCity: !!rawCity,
    };
  };

  const toggleCompanyPanel = (rowId) => {
    setOpenCompanyRowId((prev) => (prev === rowId ? null : rowId));
  };

  const allSelected =
    selectedProspects.length === (prospects?.length || 0) && prospects?.length > 0;

  return (
    <div className="pf-view">
      {loading && prospects.length === 0 ? (
        <LeadgenPreloader />
      ) : prospects.length > 0 ? (
        <>
          {/* ── TOOLBAR ── */}
          <div className="pf-toolbar">
            {searchType === 'prospects' && (
              <div className={`pf-sel-pill ${selectedProspects.length > 0 ? 'active' : ''}`}>
                <div className="pf-sel-dot" />
                <span>
                  Selected: <strong>{selectedProspects.length}</strong>
                </span>
              </div>
            )}
            <div className="pf-spacer" />
            {searchType === 'prospects' && selectedProspects.length > 0 && (
              <div className="pf-enrich-wrap" ref={enrichDropdownRef}>
                <button
                  className={`pf-enrich-btn ${enriching ? 'disabled' : ''}`}
                  onClick={() => !enriching && setEnrichDropdownOpen(!enrichDropdownOpen)}
                >
                  <i className={enriching ? 'fas fa-spinner fa-spin' : 'fas fa-sparkles'} />
                  {enriching ? ' Processing...' : ' Show detail'}
                  <i
                    className={`fas fa-chevron-down pf-chevron ${enrichDropdownOpen ? 'open' : ''}`}
                  />
                </button>
                <div className={`pf-dropdown ${enrichDropdownOpen ? 'open' : ''}`}>
                  <div className="pf-dd-option" onClick={() => handleEnrichProspects('email')}>
                    <i className="fas fa-envelope" />
                    <span>
                      Email only <span className="pf-dd-credit">1 credit</span>
                    </span>
                  </div>
                  <div className="pf-dd-option" onClick={() => handleEnrichProspects('phone')}>
                    <i className="fas fa-phone-alt" />
                    <span>
                      Phone only <span className="pf-dd-credit">5 credits</span>
                    </span>
                  </div>
                  <div className="pf-dd-divider" />
                  <div className="pf-dd-option" onClick={() => handleEnrichProspects('both')}>
                    <i className="fas fa-envelope-open-text" />
                    <span>Email &amp; phone</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── TABLE ── */}
          <div className="pf-table-wrap">
            <table className="pf-table">
              <colgroup>
                <col style={{ width: '48px' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '28%' }} />
                <col />
              </colgroup>
              {searchType === 'prospects' && (
                <thead>
                  <tr>
                    <th className="col-check">
                      <input
                        type="checkbox"
                        className="pf-check"
                        checked={allSelected}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="col-name">Name</th>
                    <th className="col-co">Company</th>
                    <th className="col-data">Data points</th>
                  </tr>
                </thead>
              )}
              <tbody>
                {searchType === 'prospects'
                  ? prospects.map((prospect) => {
                      const data = getProspectData(prospect);
                      const phoneStatus = data.isPhoneRevealed
                        ? 'revealed'
                        : data.hasPhone
                          ? 'locked'
                          : 'na';
                      const emailStatus = data.isEmailRevealed
                        ? 'revealed'
                        : data.hasEmail
                          ? 'locked'
                          : 'na';
                      const isPanelOpen = openCompanyRowId === data.id;
                      const coData = getCompanyData(prospect);
                      const isEnrichingThis = enrichingCompanyId === data.id;

                      return (
                        <React.Fragment key={data.id}>
                          <tr className={isPanelOpen ? 'row-panel-open' : ''}>
                            <td className="col-check">
                              <input
                                type="checkbox"
                                className="pf-check"
                                checked={selectedProspects.includes(data.id)}
                                onChange={() => handleSelectProspect(data.id)}
                              />
                            </td>

                            {/* Person */}
                            <td>
                              <div className="pf-person">
                                <div className="pf-avatar">
                                  <img
                                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(data.name)}&size=80&background=f1f5f9&color=64748b`}
                                    alt={data.name}
                                    className="pf-avatar-img"
                                  />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div className="pf-person-name" title={data.name}>
                                    {data.name}
                                  </div>
                                  <div className="pf-person-title" title={data.jobTitle}>
                                    {data.jobTitle}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Company trigger button */}
                            <td>
                              <button
                                className={`pf-co-btn ${isPanelOpen ? 'active' : ''}`}
                                onClick={() => toggleCompanyPanel(data.id)}
                                title="View company profile"
                              >
                                <div className="pf-co-logo">
                                  <img
                                    src={
                                      data.companyLogo ||
                                      `https://ui-avatars.com/api/?name=${encodeURIComponent(data.companyName)}&size=40&background=f1f5f9&color=64748b`
                                    }
                                    alt={data.companyName}
                                    onError={(e) => {
                                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(data.companyName)}&size=40&background=f1f5f9&color=64748b`;
                                    }}
                                  />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div className="pf-co-name" title={data.companyName}>
                                    {data.companyName}
                                  </div>
                                  <div className="pf-co-location">{data.location}</div>
                                </div>
                                <i
                                  className={`fas fa-chevron-down pf-co-chevron ${isPanelOpen ? 'open' : ''}`}
                                />
                              </button>
                            </td>

                            {/* Data points */}
                            <td>
                              <div className="pf-data-row">
                                <div className={`pf-dp ${emailStatus}`}>
                                  <i className="fas fa-envelope" />
                                  {data.email && <span className="pf-dp-val">{data.email}</span>}
                                  {(data.hasEmail || data.isEmailRevealed) && (
                                    <div className="pf-popover">
                                      <div className="pf-pop-row">
                                        <i className="fas fa-envelope" />
                                        {data.isEmailRevealed ? (
                                          <span className="pf-pop-val">{data.email}</span>
                                        ) : (
                                          <>
                                            <span className="pf-pop-val pf-pop-masked">
                                              ••••••••@domain.com
                                            </span>
                                            <button
                                              className="pf-pop-btn"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleEnrichSpecific(
                                                  data.contactId || data.id,
                                                  'email'
                                                );
                                              }}
                                            >
                                              Reveal
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className={`pf-dp ${phoneStatus}`}>
                                  <i className="fas fa-phone-alt" />
                                  {data.phone && <span className="pf-dp-val">{data.phone}</span>}
                                  {(data.hasPhone || data.isPhoneRevealed) && (
                                    <div className="pf-popover">
                                      <div className="pf-pop-row">
                                        <i className="fas fa-phone-alt" />
                                        {data.isPhoneRevealed ? (
                                          <span className="pf-pop-val">{data.phone}</span>
                                        ) : (
                                          <>
                                            <span className="pf-pop-val pf-pop-masked">
                                              +XX XX-••••••••
                                            </span>
                                            <button
                                              className="pf-pop-btn"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleEnrichSpecific(
                                                  data.contactId || data.id,
                                                  'phone'
                                                );
                                              }}
                                            >
                                              Reveal
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>

                          {/* ── INLINE COMPANY PANEL ── */}
                          {isPanelOpen && (
                            <tr className="pf-panel-row">
                              <td colSpan={4} className="pf-panel-cell">
                                <div className="pf-inline-panel">
                                  {/* Panel header */}
                                  <div className="pip-head">
                                    <div className="pip-logo">
                                      <img
                                        src={
                                          coData.logoUrl ||
                                          `https://ui-avatars.com/api/?name=${encodeURIComponent(coData.companyName)}&size=80&background=f1f5f9&color=64748b`
                                        }
                                        alt={coData.companyName}
                                        onError={(e) => {
                                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(coData.companyName)}&size=80&background=f1f5f9&color=64748b`;
                                        }}
                                      />
                                    </div>
                                    <div className="pip-head-info">
                                      <div className="pip-name">{coData.companyName}</div>
                                      <div className="pip-meta">
                                        {coData.domain && coData.domain !== '' && (
                                          <a
                                            className="pip-domain"
                                            href={`https://${coData.domain}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <i className="fas fa-globe" /> {coData.domain}
                                          </a>
                                        )}
                                        {coData.location && coData.location !== '' && (
                                          <span className="pip-location">
                                            <i className="fas fa-map-pin" /> {coData.location}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Description */}
                                  {coData.description && coData.description !== '' && (
                                    <div className="pip-desc">{coData.description}</div>
                                  )}

                                  {/* Quick stats strip */}
                                  {coData.isFresh && (
                                    <div className="pip-stat-strip">
                                      <div className="pip-stat">
                                        <div className="pip-stat-label">Employees</div>
                                        <div className="pip-stat-val">
                                          {coData.employeeCount || (
                                            <span className="pip-empty"></span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="pip-stat-divider" />
                                      <div className="pip-stat">
                                        <div className="pip-stat-label">Revenue</div>
                                        <div className="pip-stat-val">
                                          {coData.revenueValue || (
                                            <span className="pip-empty"></span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="pip-stat-divider" />
                                      <div className="pip-stat">
                                        <div className="pip-stat-label">Industry</div>
                                        <div className="pip-stat-val">
                                          {coData.mainIndustry || (
                                            <span className="pip-empty"></span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="pip-stat-divider" />
                                      <div className="pip-stat">
                                        <div className="pip-stat-label">Type</div>
                                        <div className="pip-stat-val">
                                          {coData.companyType || (
                                            <span className="pip-empty"></span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Intelligence grid */}
                                  {coData.isFresh && (
                                    <div className="pip-grid">
                                      {[
                                        {
                                          icon: 'fa-flag',
                                          label: 'Country',
                                          val: coData.country,
                                          has: coData.hasCountry,
                                        },
                                        {
                                          icon: 'fa-map-marker-alt',
                                          label: 'City',
                                          val: coData.city,
                                          has: coData.hasCity,
                                        },
                                        {
                                          icon: 'fa-tags',
                                          label: 'Sub-industry',
                                          val: coData.subIndustry,
                                          has: coData.hasSubIndustry,
                                        },
                                        {
                                          icon: 'fa-rocket',
                                          label: 'Funding',
                                          val: coData.funding,
                                          has: coData.hasFunding,
                                        },
                                      ].map(({ icon, label, val, has }) => (
                                        <div
                                          key={label}
                                          className={`pip-dc ${has ? 'has' : 'empty'}`}
                                        >
                                          <i className={`fas ${icon} pip-dc-icon`} />
                                          <div>
                                            <div className="pip-dc-label">{label}</div>
                                            <div className="pip-dc-val">
                                              {val || <span className="pip-empty"></span>}
                                            </div>
                                          </div>
                                          {has && <i className="fas fa-check pip-dc-check" />}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  : prospects.map((company) => {
                      const data = getCompanyData(company);
                      const isPanelOpen = openCompanyRowId === data.id;
                      const isEnrichingThis = enrichingCompanyId === data.id;
                      return (
                        <React.Fragment key={data.id}>
                          <tr
                            className={isPanelOpen ? 'row-panel-open' : ''}
                            onClick={() => toggleCompanyPanel(data.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            {searchType === 'prospects' && (
                              <td
                                className="col-check"
                                onClick={(e) => e.stopPropagation()}
                                style={{ width: '48px' }}
                              >
                                <input
                                  type="checkbox"
                                  className="pf-check"
                                  checked={selectedProspects.includes(data.id)}
                                  onChange={() => handleSelectProspect(data.id)}
                                />
                              </td>
                            )}
                            <td
                              colSpan={searchType === 'prospects' ? 3 : 4}
                              className="col-card-cell"
                              style={{
                                textAlign: 'left',
                                paddingLeft: searchType === 'companies' ? '20px' : '0',
                              }}
                            >
                              <div className="pf-company-card-row">
                                <div className="pf-co-logo-large">
                                  <img
                                    src={
                                      data.logoUrl ||
                                      `https://ui-avatars.com/api/?name=${encodeURIComponent(data.companyName)}&size=80&background=f1f5f9&color=64748b`
                                    }
                                    alt={data.companyName}
                                    onError={(e) => {
                                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(data.companyName)}&size=80&background=f1f5f9&color=64748b`;
                                    }}
                                  />
                                </div>
                                <div className="pf-co-info-stack">
                                  <div className="pf-co-name-large">{data.companyName}</div>
                                  {data.domain && (
                                    <div className="pf-co-url-large">
                                      <i className="fas fa-globe" /> {data.domain}
                                    </div>
                                  )}
                                </div>
                                <i
                                  className={`fas fa-chevron-down pf-co-chevron-large ${isPanelOpen ? 'open' : ''}`}
                                />
                              </div>
                            </td>
                          </tr>

                          {isPanelOpen && (
                            <tr className="pf-panel-row">
                              <td colSpan={4} className="pf-panel-cell">
                                <div className="pf-inline-panel">
                                  {data.description && data.description !== '' && (
                                    <div className="pip-desc">{data.description}</div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
              </tbody>
            </table>

            {hasMore && (
              <div className="pf-load-more">
                <button
                  className={`pf-load-more-btn ${loading ? 'disabled' : ''}`}
                  onClick={() => !loading && onLoadMore()}
                  disabled={loading}
                >
                  <i className={loading ? 'fas fa-spinner fa-spin' : 'fas fa-chevron-down'} />
                  {loading ? ' Loading more...' : ' Load More Results'}
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="pf-empty">
          <div className="pf-empty-icon">
            <i className="fas fa-search" />
          </div>
          <div className="pf-empty-title">No prospects found</div>
          <div className="pf-empty-sub">Try adjusting your search terms or filters.</div>
        </div>
      )}

      {/* ── CONFIRM MODAL ── */}
      {showConfirmModal && (
        <div className="pf-backdrop" onClick={() => setShowConfirmModal(false)}>
          <div className="pf-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="pf-confirm-icon">
              <i className="fas fa-sparkles" />
            </div>
            <div className="pf-confirm-title">{confirmConfig.title}</div>
            <div className="pf-confirm-msg">{confirmConfig.message}</div>
            <div className="pf-confirm-actions">
              <button className="pf-btn-cancel" onClick={() => setShowConfirmModal(false)}>
                Cancel
              </button>
              <button className="pf-btn-confirm" onClick={confirmConfig.onConfirm}>
                Confirm &amp; Enrich
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}
